import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import { isUsersTab, usersShellQuery, type UsersTab } from '../../api/settingsUsers'
import { Forbidden } from '../../ui/Forbidden'
import { Spinner } from '../../ui/Spinner'
import { AccountsPanel } from './AccountsPanel'
import { EventsPanel } from './EventsPanel'
import { GroupsPanel } from './GroupsPanel'
import { PlansPanel } from './PlansPanel'
import { ResourcesPanel } from './ResourcesPanel'
import { RolesPanel } from './RolesPanel'
import { SignaturesPanel } from './SignaturesPanel'

/**
 * Settings -> User Accounts: banda de sus si cele sapte ecrane ale ei.
 *
 * Perechea veche e `views/version-3/settings/users/index.php`, banda cu
 * `od-tabbar-grouped` - doua grupuri, cu titlul deasupra si o linie intre ele:
 *
 *   Users                          Roles & Resources Permissions
 *     Accounts -> Active             Resources Management
 *              -> Blocked            Roles and Resources
 *     Account Groups
 *     Events
 *     Signatures
 *     Work Hour Plans
 *
 * "Accounts" nu e o fila, ci un grup de doua: in banda orizontala ele n-au unde
 * sa se aseze (un `ul` cuibarit ori curge in rand ca si cum ar fi file de sine
 * statatoare, ori nu se vede deloc), deci devin o lista care se desface. Iar
 * butonul spune si pe care din cele doua esti - altfel, inchisa, lista ar
 * ascunde tocmai raspunsul la "unde sunt".
 *
 * Regulile de asezare sunt deja in `public/css/settings_console.css`, sectiunea
 * benzii grupate; de-aia numele claselor sunt exact cele din macheta veche.
 *
 * FILELE VIN FILTRATE PE DREPTURI DE PE SERVER. Al doilea grup, in portal, se
 * scria numai daca omul avea `settings.roles.delete` - aici asta hotaraste
 * serverul, si daca grupul ramane fara file nu se deseneaza deloc.
 */
export function UserAccountsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(usersShellQuery)

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isUsersTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/users_accounts/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const current = isUsersTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  /* Grupurile, in ordinea in care apar filele. */
  const groups: { name: string; tabs: UsersTab[] }[] = []
  for (const item of shell.data.tabs) {
    const last = groups[groups.length - 1]
    if (last && last.name === item.group) last.tabs.push(item)
    else groups.push({ name: item.group, tabs: [item] })
  }

  return (
    <>
      <nav className="od-tabbar od-tabbar-grouped">
        <ul className="od-tabbar-nav">
          {groups.map((group) => (
            <li className="od-tabgroup" key={group.name}>
              <span className="od-tabgroup-title">{group.name}</span>
              <ul className="od-tabgroup-tabs">
                <GroupTabs tabs={group.tabs} current={current} />
              </ul>
            </li>
          ))}
        </ul>
      </nav>

      <div className="page-content-wrapper">
        {(current === 'active' || current === 'blocked' || current === 'inactive') && (
          <AccountsPanel key={current} status={current} shell={shell.data} />
        )}
        {current === 'groups' && <GroupsPanel shell={shell.data} />}
        {current === 'events' && <EventsPanel />}
        {current === 'signature' && <SignaturesPanel shell={shell.data} />}
        {current === 'work_hour_plans' && <PlansPanel shell={shell.data} />}
        {current === 'resources_management' && <ResourcesPanel />}
        {current === 'userRoles' && <RolesPanel shell={shell.data} />}
      </div>
    </>
  )
}

/**
 * Filele unui grup: cele cu `parent` intra intr-o lista care se desface, la
 * locul primeia din ele; restul sunt legaturi obisnuite.
 */
function GroupTabs({ tabs, current }: { tabs: UsersTab[]; current: string }) {
  const drops = new Map<string, UsersTab[]>()
  const flat: (UsersTab | { drop: string })[] = []

  for (const tab of tabs) {
    if (tab.parent === '') {
      flat.push(tab)
      continue
    }
    const list = drops.get(tab.parent)
    if (list) {
      list.push(tab)
    } else {
      drops.set(tab.parent, [tab])
      flat.push({ drop: tab.parent })
    }
  }

  return (
    <>
      {flat.map((item) =>
        'drop' in item ? (
          <TabDrop key={item.drop} label={item.drop} tabs={drops.get(item.drop) ?? []} current={current} />
        ) : (
          <TabLink key={item.id} tab={item} current={current} />
        ),
      )}
    </>
  )
}

function TabLink({ tab, current }: { tab: UsersTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const go = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/users_accounts/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.id} href={'/app/settings/users_accounts/' + tab.id} onClick={go}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/** "Accounts": Active si Blocked, cu numele filei deschise scris pe buton. */
function TabDrop({ label, tabs, current }: { label: string; tabs: UsersTab[]; current: string }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const here = tabs.find((tab) => tab.id === current)

  useEffect(() => {
    if (!open) return
    const away = () => setOpen(false)
    document.addEventListener('click', away)
    return () => document.removeEventListener('click', away)
  }, [open])

  return (
    <li className={`od-tabdrop${open ? ' is-open' : ''}${here ? ' active' : ''}`}>
      <button
        type="button"
        className="od-tabdrop-btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((prev) => !prev)
        }}
      >
        <i className={tabs[0]?.icon ?? 'fa fa-user'} aria-hidden="true"></i> {label}
        <span className="od-tabdrop-which">{here?.label ?? ''}</span>
        <i className="fas fa-caret-down od-tabdrop-caret" aria-hidden="true"></i>
      </button>

      <ul className="od-tabdrop-menu">
        {tabs.map((tab) => (
          <li key={tab.id} className={tab.id === current ? 'menu-tab active' : 'menu-tab'}>
            <a
              href={'/app/settings/users_accounts/' + tab.id}
              onClick={(event) => {
                event.preventDefault()
                setOpen(false)
                void navigate({ to: '/settings/users_accounts/$tab', params: { tab: tab.id } })
              }}
            >
              {tab.label}
            </a>
          </li>
        ))}
      </ul>
    </li>
  )
}
